import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Toast from "react-bootstrap/Toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faImage, faFileExcel, faFile } from "@fortawesome/free-solid-svg-icons";

const Dropzone = ({ fieldName, onFileUpload, fileTypes = [], showPreview = false }) => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const MAX_FILE_SIZE = 250 * 1024 * 1024;

  const onDrop = async (acceptedFiles) => {
    setIsUploading(true);
    setToastMessage("");

    const filteredFiles = acceptedFiles.filter(file => {
      // If fileTypes is empty, accept all file types
      let isValidType = true;
      
      if (fileTypes.length > 0) {
        isValidType = fileTypes.some(type => {
          // Check MIME type if available
          if (file.type) {
            if (type.endsWith("/*")) {
              const baseType = type.split("/")[0];
              return file.type.startsWith(baseType + "/");
            }
            return file.type === type;
          }
          
          // Fallback: check file extension if MIME type is not available
          const fileName = file.name.toLowerCase();
          const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
          
          if (type === "image/*") {
            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
            return imageExtensions.includes(extension);
          }
          if (type === "video/*") {
            const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp'];
            return videoExtensions.includes(extension);
          }
          
          return false;
        });
      }
    
      const isValidSize = file.size <= MAX_FILE_SIZE;
      return isValidType && isValidSize;
    });

    if (filteredFiles.length === 0) {
      setToastMessage("file were too large or had invalid types (Max 250 MB).");
      setShowToast(true);
      setIsUploading(false);
      return;
    }

    const existingFileNames = files.map(file => file.name);
    const uniqueFiles = filteredFiles.filter(file => !existingFileNames.includes(file.name));

    if (uniqueFiles.length === 0) {
      setToastMessage("Duplicate file detected! This file is already uploaded.");
      setShowToast(true);
      setIsUploading(false);
      return;
    }

    try {

      const uploadedIds = await uploadFilesToServer(uniqueFiles);
      setFiles((prev) => [...prev, ...uniqueFiles]);
      setUploadedFileIds((prev) => [...prev, ...uploadedIds]);
      onFileUpload(fieldName, [...uploadedFileIds, ...uploadedIds]);
      setToastMessage("File(s) uploaded successfully!");
      setShowToast(true);
    } catch (error) {
      console.error("File upload failed:", error);
      setToastMessage("Failed to upload files. Please try again.");
      setShowToast(true);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFilesToServer = (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return fetch(`${process.env.REACT_APP_API_URL}/attachments/upload-files`, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          const documentIds = data.documentIds || [];
          return Array.isArray(documentIds) ? documentIds : [documentIds];
        } else {
          throw new Error(data.message || "File upload failed");
        }
      })
      .catch((error) => {
        console.error("Error during file upload:", error);
        throw error;
      });
  };

  // Build accept object for react-dropzone
  // Expand wildcards to specific MIME types for better browser compatibility
  const acceptConfig = fileTypes.length > 0 ? fileTypes.reduce((acc, type) => {
    if (type === 'image/*') {
      // Expand image/* to common image MIME types
      acc['image/jpeg'] = ['.jpg', '.jpeg'];
      acc['image/png'] = ['.png'];
      acc['image/gif'] = ['.gif'];
      acc['image/webp'] = ['.webp'];
      acc['image/bmp'] = ['.bmp'];
      acc['image/svg+xml'] = ['.svg'];
      acc['image/x-icon'] = ['.ico'];
    } else if (type === 'video/*') {
      // Expand video/* to common video MIME types
      acc['video/mp4'] = ['.mp4'];
      acc['video/quicktime'] = ['.mov'];
      acc['video/x-msvideo'] = ['.avi'];
      acc['video/x-ms-wmv'] = ['.wmv'];
      acc['video/x-flv'] = ['.flv'];
      acc['video/webm'] = ['.webm'];
      acc['video/x-matroska'] = ['.mkv'];
      acc['video/mp4'] = ['.m4v'];
      acc['video/3gpp'] = ['.3gp'];
    } else {
      // For specific MIME types, use them as keys
      acc[type] = [];
    }
    return acc;
  }, {}) : undefined;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptConfig,
    multiple: true, // Allow multiple files
    noClick: false, // Allow clicking to select files
    noKeyboard: false, // Allow keyboard navigation
  });

  const handleRemoveFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
  
    const updatedFileIds = [...uploadedFileIds];
    updatedFileIds.splice(index, 1);
  
    setFiles(updatedFiles);
    setUploadedFileIds(updatedFileIds);
  
    onFileUpload(fieldName, updatedFileIds);
  };

  // Function to render the file icon based on file type
  const getFileIcon = (file) => {
    if (file.type?.startsWith("image/")) {
      return <FontAwesomeIcon icon={faImage} size="2x" style={{ color: "blue" }} />;
    } else if (file.type === "application/pdf") {
      return <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "red" }} />;
    } else if (
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <FontAwesomeIcon icon={faFileExcel} size="2x" style={{ color: "green" }} />;
    } else {
      return <FontAwesomeIcon icon={faFile} size="2x" style={{ color: "black" }} />;
    }
  };

  return (
    <div className="px-0">
      {isUploading && (
        <div className="fullscreen-loader">
          <Spinner animation="border" variant="light" />
          <div className="loading-text">Uploading...</div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 1050,
        }}
      >
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg={toastMessage.includes("success") ? "success" : "warning"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastMessage.includes("success") ? "Success" : "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body
            className={toastMessage.includes("success") ? "text-white" : ""}
          >
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      <Row className="no-gutters">
        <Col sm="12">
          <div className="dropzone-container mt-3">
            <div
              className="d-flex flex-column align-items-center dropzone-wrapper"
              style={{ color: isDragActive ? "#007bff" : "#333" }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {isUploading ? (
                <div className="loading-container">
                  <Spinner animation="border" />
                  <div className="mt-2">Uploading...</div>
                </div>
              ) : isDragActive ? (
                <div className="drpzone-internal">
                  <div className="my-2">
                    <img src="../backup.png" alt="Backup" />
                  </div>
                  <div className="mb-2 drag-text">Drop the files here ...</div>
                  <div className="max-length mb-2">Max 250 MB files are allowed</div>
                </div>
              ) : (
                <div className="drpzone-internal">
                  <div className="my-2">
                    <img src={`${process.env.PUBLIC_URL}/backup.svg`} alt="Backup" />
                  </div>
                  <div className="mb-2 drag-text">
                    Drag your file(s) or <span>browse</span>
                  </div>
                  <div className="max-length mb-2">Max 250 MB files are allowed</div>
                </div>
              )}
            </div>

            {/* Display Uploaded Files and Previews */}
            <div className="uploaded-files mt-3">
              {files.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {files.map((file, index) => (
                     <li key={index} className="file-item d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #ddd", padding: "10px 0" }}>
                     <div className="d-flex align-items-center">
                       <div className="file-icon me-2">{getFileIcon(file)}</div>
                       <span style={{ fontSize: "14px", color: "#333" }}>{file.name}</span>
                     </div>
                     <button type="button" className="btn btn-sm btn-danger ms-2" onClick={() => handleRemoveFile(index)}>Remove</button>
                   </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Dropzone;